import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Dialog, InputBase, Typography, alpha, useTheme } from "@mui/material";
import { KeyboardReturn, Search } from "@mui/icons-material";
import { NavPage, useNavigation } from "./useNavigation";

export const JUMP_SHORTCUT =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl K";

/** Title matches first, then anything whose title or place mentions every word. */
const rank = (pages: NavPage[], query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return pages;
  const words = q.split(/\s+/);
  const score = (p: NavPage) => {
    const title = p.title.toLowerCase();
    if (title.startsWith(q)) return 0;
    if (title.includes(q)) return 1;
    return 2;
  };
  return pages
    .filter((p) => {
      const hay = `${p.title} ${p.trail}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    .sort((a, b) => score(a) - score(b));
};

const Key: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: "inherit",
      fontSize: "0.68rem",
      fontWeight: 600,
      minWidth: 20,
      px: 0.6,
      py: 0.1,
      textAlign: "center",
      borderRadius: "5px",
      border: 1,
      borderColor: "divider",
      bgcolor: "background.paper",
      color: "text.secondary",
    }}
  >
    {children}
  </Box>
);

/** Jump to any page this user can open, by typing part of its name. */
const NavJumpDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pages, activePath } = useNavigation();
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => rank(pages, query), [pages, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setIndex(0);
    }
  }, [open]);
  useEffect(() => setIndex(0), [query]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" });
  }, [index]);

  const choose = (page: NavPage | undefined) => {
    if (!page) return;
    onClose();
    navigate(page.path);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(results[index]);
    }
  };

  const tint = alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.08);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      PaperProps={{
        sx: {
          mt: { xs: 2, sm: "12vh" },
          mx: 2,
          width: "calc(100% - 32px)",
          borderRadius: "14px",
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          boxShadow: theme.shadows[16],
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, height: 56, borderBottom: 1, borderColor: "divider" }}>
        <Search sx={{ color: "text.secondary", fontSize: 22 }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search pages"
          fullWidth
          inputProps={{ "aria-label": "Search pages", role: "combobox", "aria-expanded": true }}
          sx={{ fontSize: "1rem" }}
        />
        <Key>Esc</Key>
      </Box>

      <Box ref={listRef} role="listbox" sx={{ maxHeight: "min(420px, 60vh)", overflowY: "auto", p: 0.75 }}>
        {results.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>No page matches "{query.trim()}"</Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mt: 0.5 }}>
              Try a shorter word, like "fee" or "exam".
            </Typography>
          </Box>
        ) : (
          results.map((page, i) => {
            const selected = i === index;
            return (
              <Box
                key={`${page.trail}:${page.path}`}
                data-index={i}
                role="option"
                aria-selected={selected}
                onMouseMove={() => i !== index && setIndex(i)}
                onClick={() => choose(page)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.25,
                  height: 44,
                  borderRadius: "8px",
                  cursor: "pointer",
                  bgcolor: selected ? tint : "transparent",
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: "7px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: 1,
                    borderColor: selected ? "transparent" : "divider",
                    bgcolor: selected ? "primary.main" : "background.paper",
                    color: selected ? "primary.contrastText" : "text.secondary",
                    "& svg": { fontSize: 16 },
                  }}
                >
                  {page.icon}
                </Box>
                <Typography noWrap sx={{ fontSize: "0.875rem", fontWeight: selected ? 600 : 500, color: "text.primary" }}>
                  {page.title}
                </Typography>
                {page.path === activePath && (
                  <Typography sx={{ fontSize: "0.7rem", color: "primary.main", fontWeight: 600 }}>You are here</Typography>
                )}
                <Typography noWrap sx={{ ml: "auto", pl: 1, fontSize: "0.75rem", color: "text.disabled" }}>
                  {page.trail}
                </Typography>
                <KeyboardReturn sx={{ fontSize: 15, color: "text.secondary", visibility: selected ? "visible" : "hidden" }} />
              </Box>
            );
          })
        )}
      </Box>

      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: alpha(theme.palette.text.primary, 0.02),
          fontSize: "0.72rem",
          color: "text.secondary",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Key>↑</Key><Key>↓</Key> to move</Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Key>Enter</Key> to open</Box>
        <Box sx={{ ml: "auto" }}>{results.length} {results.length === 1 ? "page" : "pages"}</Box>
      </Box>
    </Dialog>
  );
};

export default NavJumpDialog;
